import { forwardRef, type CSSProperties } from 'react';

/* Tarjeta de RESULTADOS DE UNA SERIE para publicar en Telegram (modo admin).
 *
 * Réplica en CLARO (decisión 2026-07-21: nuestro canal publica en claro, no
 * en el oscuro del directo de RFEA) con la paleta retro de Mundo Atletismo:
 * crema + tinta + naranja, Big Shoulders/Space Mono (ya cargadas en
 * global.css). Se rasteriza con html2canvas fuera de pantalla (mismo patrón
 * que FichaVivoCard) y la Cloud Function publish_telegram la envía al canal.
 *
 * Anchura fija (1080) y altura según filas: sendPhoto admite hasta
 * width+height ≤ 10000 y ratio ≤ 20, holgado incluso con 30 atletas.
 */

export const TG_CARD_W = 1080;
/** Ancho del lienzo a dos columnas (tarjetas muy verticales). */
export const TG_CARD_W2 = 1720;

/** Layout de la tarjeta según su carga: si el cuerpo estimado supera ~2×ancho
 *  (Telegram encogería la imagen y no ocuparía el ancho del bocadillo, Riki
 *  2026-07-21), se pasa a DOS COLUMNAS en un lienzo de 1720. Misma regla que
 *  render_serie_png en el servidor. */
export function tgCardLayout(filas: TgFila[]): { width: number; dosCol: boolean } {
  const cuerpo = filas.reduce((a, f) => a + 106 + (f.chips?.length ? 68 : 0), 0);
  const dosCol = 210 + cuerpo + 64 > 2 * TG_CARD_W && filas.length >= 6;
  return { width: dosCol ? TG_CARD_W2 : TG_CARD_W, dosCol };
}

/** Cajita de la línea de detalle de una fila: intento de un concurso
 *  ("7.47"), listón + saltos ("1.95" / "XXO") o prueba de combinada
 *  (marca / puntos). `hi` = destacada en naranja (mejor intento / listón
 *  superado). */
export interface TgChip {
  t: string;
  b?: string | null;
  hi?: boolean;
  /** Aviso del juez de marcha: chip RELLENO rojo con el símbolo en blanco
   *  (calcado a los badges de rfealive, 2026-07-24). */
  rojo?: boolean;
}

/** Fila de la tarjeta. `irm` (DNS/DNF/DQ/NM) pinta chip rojo y sin puesto. */
export interface TgFila {
  puesto: string;
  nombre: string;
  club: string;
  marca: string;
  /** "Q" (pasa por puestos) / "q" (por tiempos) / null. */
  quali?: string | null;
  irm?: string | null;
  dorsal?: string | null;
  categoria?: string | null;
  viento?: string | null;
  /** Serie de origen (solo en la tarjeta de clasificados): "S1", "S3"… */
  serie?: string | null;
  /** Código IOC del país ("ESP") — fuentes EA/WA: pinta la bandera. */
  pais?: string | null;
  /** Medalla de la final ("1"|"2"|"3"): la asigna el modal excluyendo a los
   *  fuera de concurso (FC) — NO siempre coincide con `puesto`. */
  medalla?: string | null;
  /** Intentos/listones/pruebas de la fila (concursos y combinadas). */
  chips?: TgChip[];
  /** Récords/mejores marcas batidos: stickers junto al nombre, en orden de
   *  importancia (["LE","MMP"]…). RE/RC/LE en oro, MMP/MMT en ámbar. */
  records?: string[];
}

export interface TelegramSerieCardProps {
  competicion: string;
  /** "100 m Hombres" */
  titulo: string;
  /** "Eliminatoria 1/4 · Oficial" / "Clasificados a la Final" */
  subtitulo: string;
  /** Viento de la serie ("+0.5"), si aplica. */
  viento?: string | null;
  filas: TgFila[];
  /** Final: medallas 🥇🥈🥉 en los tres primeros. */
  medallas?: boolean;
  /** Pie con la regla de calificación ("Q = 3 primeros de cada serie…"). */
  nota?: string | null;
  /** EA: foto de fondo a sangre (Alexander Stadium, ya lavada en crema) y logo
   *  del Europeo en la esquina, para que el manual replique a las automáticas. */
  fondo?: string | null;
  logo?: string | null;
}

/* ─── Versión TEXTO de la publicación (beta A/B 2026-07-22) ─────────────────
 * Mensaje HTML de Telegram equivalente a la tarjeta, construido de las MISMAS
 * props. Paridad con texto_publicacion() de functions/auto_directo.py: el
 * botón manual duplica cada post (imagen + texto) para que los usuarios beta
 * comparen formatos. */

function escHtml(s: string): string {
  return (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function chipsTxt(chips?: TgChip[]): string {
  if (!chips?.length) return '';
  const usadas = chips.slice(-6);
  const partes = (usadas.length < chips.length ? ['…'] : []).concat(
    usadas.map((c) => (c.b ? `${c.t}:${c.b}` : c.t)),
  );
  return ` (${partes.join(' · ')})`;
}

export function textoPublicacion(p: TelegramSerieCardProps): string {
  const MEDAL: Record<string, string> = { '1': '🥇', '2': '🥈', '3': '🥉' };
  let sub = p.subtitulo || '';
  if (p.viento) sub += ` · Viento ${p.viento}`;
  const lineas = [
    `⚡ <b>${escHtml((p.titulo || '').toUpperCase())}</b> — ${escHtml(sub)}`,
    escHtml(p.competicion || ''),
    '',
  ];
  for (const f of p.filas) {
    const quien =
      `<b>${escHtml(f.nombre)}</b>` +
      (f.club ? ` (${escHtml(f.club)})` : '') +
      (f.serie ? ` · ${escHtml(f.serie)}` : '');
    if (f.irm) {
      lineas.push(`— ${quien} — <b>${escHtml(f.irm)}</b>`);
      continue;
    }
    const pos = (p.medallas && f.medalla && MEDAL[f.medalla]) || (f.puesto ? `${f.puesto}.` : '—');
    const quali = f.quali ? ` [${escHtml(f.quali)}]` : '';
    lineas.push(`${pos} ${quien} — <b>${escHtml(f.marca || '—')}</b>${quali}${escHtml(chipsTxt(f.chips))}`);
  }
  lineas.push('');
  if (p.nota) lineas.push(escHtml(p.nota));
  lineas.push('📊 resultados.mundoatletismo.es');
  let texto = lineas.join('\n');
  // Límite de sendMessage (4096): primero fuera los intentos, luego filas.
  if (texto.length > 4000 && p.filas.some((f) => f.chips?.length)) {
    texto = textoPublicacion({ ...p, filas: p.filas.map((f) => ({ ...f, chips: undefined })) });
  }
  while (texto.length > 4000) {
    const ls = texto.split('\n');
    texto = ls.slice(0, ls.length - 4).concat(['…', '📊 resultados.mundoatletismo.es']).join('\n');
  }
  return texto;
}

/** Versión WHATSAPP del texto: mismo contenido que el de Telegram, pero con
 *  el formato nativo de WhatsApp (*negrita*) en vez de HTML, y SIN la línea
 *  de la URL (2026-07-23: en WhatsApp repetirla en cada post era pesado y
 *  además genera tarjeta de preview). Se deriva del mensaje de Telegram ya
 *  construido para que ambos canales publiquen lo mismo. */
export function textoWhatsapp(htmlTelegram: string): string {
  return htmlTelegram
    .replace(/<\/?b>/g, '*')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .split('\n')
    .filter((l) => !l.startsWith('📊'))
    .join('\n')
    .replace(/\n+$/, '');
}

const DANGER = '#c53a07';
const STICKER_ORO = '#e7b539';
const STICKER_AMBAR = '#bc8a1c';
const INK = '#1a1410';
const MUTED = '#7a6a5c';
const ACCENT = '#e84b0e';
const BG = '#f0dcc1';
const SURFACE = '#f8ead6';
const LINE = 'rgba(26, 20, 16, 0.22)';
/* Código IOC → ISO-3166 alpha-2, para la bandera emoji (los IOC no siempre
 * coinciden con ISO: GER→DE, DEN→DK, SUI→CH…). País no mapeado → sin bandera. */
const IOC_ISO2: Record<string, string> = {};
('ESP:ES GBR:GB GER:DE FRA:FR ITA:IT POR:PT NED:NL BEL:BE SUI:CH AUT:AT SWE:SE NOR:NO DEN:DK FIN:FI ISL:IS IRL:IE '
+ 'POL:PL CZE:CZ SVK:SK HUN:HU ROU:RO BUL:BG GRE:GR CRO:HR SLO:SI SRB:RS BIH:BA MKD:MK ALB:AL MNE:ME KOS:XK TUR:TR '
+ 'UKR:UA BLR:BY EST:EE LAT:LV LTU:LT MDA:MD ARM:AM GEO:GE AZE:AZ CYP:CY MLT:MT LUX:LU LIE:LI MON:MC AND:AD SMR:SM '
+ 'GIB:GI ISR:IL USA:US CAN:CA MEX:MX JAM:JM CUB:CU DOM:DO PUR:PR TTO:TT BAH:BS BAR:BB GUY:GY SUR:SR BRA:BR ARG:AR '
+ 'CHI:CL COL:CO PER:PE ECU:EC VEN:VE URU:UY BOL:BO PAR:PY PAN:PA CRC:CR GUA:GT HON:HN ESA:SV NCA:NI HAI:HT RSA:ZA '
+ 'KEN:KE ETH:ET UGA:UG TAN:TZ NGR:NG GHA:GH CIV:CI SEN:SN MAR:MA ALG:DZ TUN:TN EGY:EG BOT:BW ZAM:ZM ZIM:ZW MOZ:MZ '
+ 'NAM:NA ERI:ER BDI:BI RWA:RW SUD:SD GAM:GM BUR:BF MLI:ML NIG:NE CMR:CM GAB:GA CGO:CG COD:CD ANG:AO LBR:LR SLE:SL '
+ 'TOG:TG BEN:BJ CHA:TD CAF:CF SOM:SO DJI:DJ LES:LS SWZ:SZ MAW:MW MRI:MU SEY:SC CPV:CV MTN:MR LBA:LY GEQ:GQ GBS:GW '
+ 'STP:ST COM:KM MAD:MG CHN:CN JPN:JP KOR:KR PRK:KP TPE:TW HKG:HK IND:IN PAK:PK SRI:LK BAN:BD NEP:NP AFG:AF IRI:IR '
+ 'IRQ:IQ KSA:SA UAE:AE QAT:QA KUW:KW BRN:BH OMA:OM YEM:YE JOR:JO LBN:LB SYR:SY PLE:PS KAZ:KZ UZB:UZ TKM:TM TJK:TJ '
+ 'KGZ:KG MGL:MN VIE:VN THA:TH MAS:MY SGP:SG INA:ID PHI:PH MYA:MM CAM:KH LAO:LA BRU:BN TLS:TL AUS:AU NZL:NZ FIJ:FJ '
+ 'PNG:PG SAM:WS TGA:TO VAN:VU SOL:SB ISV:VI IVB:VG CAY:KY BER:BM ANT:AG GRN:GD LCA:LC VIN:VC SKN:KN DMA:DM ARU:AW')
  .split(' ').forEach((par) => { const [ioc, iso] = par.split(':'); IOC_ISO2[ioc] = iso; });

/** Bandera emoji desde el código IOC ('' si no se conoce el país). */
function bandera(ioc?: string | null): string {
  const iso = IOC_ISO2[(ioc ?? '').trim().toUpperCase()];
  if (!iso) return '';
  return String.fromCodePoint(...[...iso].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

/** Pictograma de pista (los 3 rectángulos redondeados de icon-pista-*.svg)
 *  dibujado con DIVs + border-radius: html2canvas rasteriza mal los <img> SVG
 *  (2026-07-25: los óvalos salían como arcos en la tarjeta publicada) y los
 *  bordes CSS los clava. Misma geometría que render_podio._draw_pista. */
function PictogramaPista({ size = 54 }: { size?: number }) {
  const sc = size / 100;
  const rect = (x: number, y: number, w: number, h: number, r: number, color: string, sw: number): CSSProperties => ({
    position: 'absolute', left: x * sc, top: y * sc, width: w * sc, height: h * sc,
    borderRadius: r * sc, border: `${Math.max(2, Math.round(sw * sc))}px solid ${color}`,
    boxSizing: 'border-box',
  });
  return (
    <div style={{ position: 'relative', width: size, height: size, flex: 'none' }} aria-hidden>
      <div style={rect(10, 18, 80, 64, 32, INK, 4.5)} />
      <div style={rect(20, 28, 60, 44, 22, INK, 4.5)} />
      <div style={rect(30, 38, 40, 24, 12, ACCENT, 5)} />
    </div>
  );
}

const DISPLAY = "'Big Shoulders Display', 'Archivo Black', system-ui, sans-serif";
const SANS = "'Space Grotesk', system-ui, sans-serif";
const MONO = "'Space Mono', 'JetBrains Mono', ui-monospace, monospace";

function medalla(puesto: string): string | null {
  if (puesto === '1') return '🥇';
  if (puesto === '2') return '🥈';
  if (puesto === '3') return '🥉';
  return null;
}

/** Chip Q/q: la "Q" grande (por puestos) va sólida; la "q" (por tiempos),
 *  perfilada — misma distinción que hace el directo. */
function QualiChip({ quali }: { quali: string }) {
  const grande = quali === 'Q';
  return (
    <span
      style={{
        fontFamily: MONO, fontWeight: 700, fontSize: 30, lineHeight: 1,
        padding: '8px 16px', borderRadius: 8, minWidth: 56, textAlign: 'center',
        color: grande ? SURFACE : ACCENT,
        background: grande ? ACCENT : 'transparent',
        border: `3px solid ${ACCENT}`,
      }}
    >
      {quali}
    </span>
  );
}

export const TelegramSerieCard = forwardRef<HTMLDivElement, TelegramSerieCardProps>(
  function TelegramSerieCard({ competicion, titulo, subtitulo, viento, filas, medallas, nota, fondo, logo }, ref) {
    const layout = tgCardLayout(filas);
    return (
      <div
        ref={ref}
        style={{
          width: layout.width,
          // EA: foto del estadio a sangre (ya viene lavada en crema); sin fondo,
          // el degradado crema de siempre.
          ...(fondo
            ? { backgroundImage: `url(${fondo})`, backgroundSize: 'cover', backgroundPosition: 'center' }
            : { background: `linear-gradient(180deg, #f4e3cc, ${BG})` }),
          color: INK,
          fontFamily: SANS,
          padding: '36px 40px 28px',
          boxSizing: 'border-box',
        }}
      >
        {/* Cabecera de marca */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 18 }}>
          <PictogramaPista size={54} />
          <span style={{ fontFamily: DISPLAY, fontWeight: 900, fontSize: 34, letterSpacing: '0.02em', textTransform: 'uppercase' }}>
            Mundo Atletismo
          </span>
          <span style={{ flex: 1 }} />
          {logo ? (
            <img src={logo} alt="" style={{ height: 78, width: 'auto', display: 'block' }} />
          ) : (
            <span style={{ fontFamily: MONO, fontSize: 20, color: MUTED }}>{competicion}</span>
          )}
        </div>

        {/* Título de la prueba */}
        <div style={{ borderLeft: `8px solid ${ACCENT}`, padding: '4px 0 4px 18px', marginBottom: 22 }}>
          <div style={{ fontFamily: DISPLAY, fontWeight: 900, fontSize: 52, lineHeight: 1.05, textTransform: 'uppercase' }}>
            {titulo}
          </div>
          <div style={{ fontFamily: MONO, fontSize: 24, color: ACCENT, marginTop: 6, fontWeight: 700 }}>
            {subtitulo}
            {viento ? <span style={{ color: MUTED, fontWeight: 400 }}> · Viento {viento}</span> : null}
          </div>
        </div>

        {/* Filas */}
        <div
          style={
            layout.dosCol
              ? {
                  // Dos columnas rellenadas EN VERTICAL (1..n/2 izquierda,
                  // resto derecha), como el render Pillow del servidor.
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gridTemplateRows: `repeat(${Math.ceil(filas.length / 2)}, auto)`,
                  gridAutoFlow: 'column',
                  gap: 10,
                  alignItems: 'start',
                }
              : { display: 'flex', flexDirection: 'column', gap: 10 }
          }
        >
          {filas.map((f, i) => {
            const med = medallas ? medalla(f.medalla ?? '') : null;
            return (
              <div
                key={`${f.puesto}_${f.nombre}_${i}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap',
                  // EA: filas translúcidas al ~50% para que se vea el fondo del
                  // estadio detrás (patrón de las automáticas); sin fondo, sólidas.
                  background: fondo ? 'rgba(248, 234, 214, 0.5)' : SURFACE,
                  border: `1px solid ${LINE}`, borderRadius: 12,
                  padding: '14px 20px',
                }}
              >
                {/* Puesto / medalla */}
                <span style={{ fontFamily: DISPLAY, fontWeight: 900, fontSize: 42, minWidth: 66, textAlign: 'center', lineHeight: 1 }}>
                  {med ?? (f.puesto ? f.puesto : '—')}
                </span>
                {/* Nombre + club (+ serie de origen en clasificados) */}
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontFamily: DISPLAY, fontWeight: 700, fontSize: 34, lineHeight: 1.1, textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {bandera(f.pais) ? <span style={{ marginRight: 10 }}>{bandera(f.pais)}</span> : null}
                    {f.nombre}
                  </span>
                  <span style={{ display: 'block', fontSize: 21, color: MUTED, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {f.club}
                    {f.serie ? <span style={{ fontFamily: MONO }}> · {f.serie}</span> : null}
                  </span>
                </span>
                {/* Stickers de récord (RE/RC/LE en oro, MMP/MMT en ámbar) */}
                {f.records?.length ? (
                  <span style={{ display: 'flex', gap: 8 }}>
                    {f.records.map((r) => (
                      <span
                        key={r}
                        style={{
                          fontFamily: MONO, fontWeight: 700, fontSize: 22, lineHeight: 1,
                          color: INK, background: ['RE', 'RC', 'LE'].includes(r) ? STICKER_ORO : STICKER_AMBAR,
                          borderRadius: 8, padding: '10px 12px',
                        }}
                      >
                        {r}
                      </span>
                    ))}
                  </span>
                ) : null}
                {/* Dorsal + categoría */}
                {f.categoria || f.dorsal ? (
                  <span style={{ fontFamily: MONO, fontSize: 19, color: MUTED, textAlign: 'right', lineHeight: 1.3 }}>
                    {f.dorsal ? <span style={{ display: 'block' }}>#{f.dorsal}</span> : null}
                    {f.categoria ? <span style={{ display: 'block' }}>{f.categoria}</span> : null}
                  </span>
                ) : null}
                {/* Marca (o IRM) + chip Q/q */}
                {f.irm ? (
                  <span
                    style={{
                      fontFamily: MONO, fontWeight: 700, fontSize: 28, lineHeight: 1,
                      color: '#fff', background: '#c53a07', borderRadius: 8, padding: '10px 16px',
                    }}
                  >
                    {f.irm}
                  </span>
                ) : (
                  <span style={{ fontFamily: MONO, fontWeight: 700, fontSize: 40, lineHeight: 1, minWidth: 150, textAlign: 'right' }}>
                    {f.marca || '—'}
                  </span>
                )}
                {!f.irm && f.quali ? <QualiChip quali={f.quali} /> : null}
                {/* Línea de detalle: intentos del concurso / listones /
                    pruebas de la combinada, como en el directo. */}
                {f.chips?.length ? (
                  <span style={{ flexBasis: '100%', display: 'flex', gap: 8, flexWrap: 'wrap', paddingLeft: 84 }}>
                    {f.chips.map((c, j) => (
                      <span
                        key={`${c.t}_${j}`}
                        style={{
                          display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
                          border: c.rojo ? 'none' : `${c.hi ? 3 : 1}px solid ${c.hi ? ACCENT : LINE}`,
                          background: c.rojo ? DANGER : 'transparent', borderRadius: 8,
                          padding: '6px 12px', minWidth: 44, lineHeight: 1.15,
                        }}
                      >
                        <span style={{ fontFamily: MONO, fontWeight: 700, fontSize: 22, color: c.rojo ? '#fff' : c.hi ? ACCENT : INK }}>{c.t}</span>
                        {c.b ? <span style={{ fontFamily: MONO, fontSize: 15, color: c.hi ? ACCENT : MUTED }}>{c.b}</span> : null}
                      </span>
                    ))}
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>

        {/* Pie: regla de calificación + web */}
        <div style={{ display: 'flex', alignItems: 'center', marginTop: 20, gap: 12 }}>
          <span style={{ fontFamily: MONO, fontSize: 18, color: MUTED, flex: 1 }}>{nota ?? ''}</span>
          <span style={{ fontFamily: MONO, fontSize: 20, color: ACCENT, fontWeight: 700 }}>
            resultados.mundoatletismo.es
          </span>
        </div>
      </div>
    );
  },
);
